import { AntDesign } from "@expo/vector-icons";
import {
  Badge,
  ProductThumbnail,
  Stack,
  Text,
} from "@towersystems/roam-common-native";
import { useThemeProvider } from "@towersystems/roam-common-native/lib/utilities/theme";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { TouchableOpacity } from "react-native";
import { Spacings, View } from "react-native-ui-lib";
import { moneyToFormattedString } from "../../../../utilities/money";
import { toTitleCase } from "../../../../utilities/to-title-case";

export type ProductResourceListItemProps = {
  product: Product;
  action?: () => void;
  actionLabel?: string;
  paddingVertical?: number;
  componentRight?: React.ReactNode;
};

export function ProductResourceListItem({
  product,
  action,
  actionLabel,
  paddingVertical,
  componentRight,
}: ProductResourceListItemProps) {
  const { colors } = useThemeProvider();

  function getImageUrl(product: Product) {
    return product.image?.path;
  }

  const image = getImageUrl(product);

  const badges = [];

  if (product?.variantParentId) {
    if (product.variantParentId == product.id) {
      badges.push(<Badge key="MAIN_PRODUCT" productType="MAIN_PRODUCT" />);
    } else {
      badges.push(
        <Badge key="VARIANT_PRODUCT" productType="VARIANT_PRODUCT" />
      );
    }
  }

  if (!product.stockCode || product.stockCode === "0") {
    badges.push(
      <Badge key="NON-RETAILER-PRODUCT" productSource="NON-RETAILER-PRODUCT" />
    );
  }

  if (!!product.productKit) {
    badges.push(<Badge key="KIT_PRODUCT" productType="KIT_PRODUCT" />);
  }

  if (!product.active) {
    badges.push(<Badge key="DISABLED" enabledStatus="DISABLED" />);
  }

  const componentRightMarkup = componentRight ? (
    componentRight
  ) : (
    <>
      <View>
        {actionLabel ? (
          <Text variant="heading1">{actionLabel}</Text>
        ) : (
          <View style={{ alignItems: "flex-end" }}>
            <Text variant="heading1">
              {moneyToFormattedString(product.price ?? 0)}
            </Text>
          </View>
        )}
      </View>
      <AntDesign
        style={[{ marginLeft: Spacings.s4 }]}
        name="right"
        size={24}
        color={colors.text}
      />
    </>
  );

  return (
    <TouchableOpacity
      onPress={action}
      style={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        paddingVertical: paddingVertical,
      }}
    >
      <View row centerV style={{ flexBasis: "100%", flexShrink: 1 }}>
        <View style={{ marginRight: Spacings.s4 }}>
          <ProductThumbnail size="large" imageUrl={image} />
        </View>
        <View style={{ flexShrink: 1 }}>
          <Stack spacing={Spacings.s2} vertical>
            <View>
              <Text
                variant="heading"
                fontWeight="medium"
                numberOfLines={1}
                ellipsizeMode="head"
              >
                {toTitleCase(product.name)}
              </Text>
              {product.displayName ? (
                <Text color="subdued" variant="heading5">
                  {product.displayName}
                </Text>
              ) : undefined}
            </View>
            {product.hasVariants ? (
              <View>
                <Text
                  color="subdued"
                  variant="heading5"
                >{`${product.totalVariants} Variants`}</Text>
              </View>
            ) : (
              <View>
                <Text color="subdued" variant="heading5">
                  {product.stockNumber}
                </Text>
              </View>
            )}
            <Stack>{badges}</Stack>
          </Stack>
        </View>
      </View>
      <View style={{ paddingLeft: Spacings.s6 }} row centerV>
        {componentRightMarkup}
      </View>
    </TouchableOpacity>
  );
}
